/**
 * 后台任务状态管理
 *
 * NOTE: 此 store 当前为预留实现，尚未被任何组件消费。
 * 后台任务的实时输出/状态展示实际由 chatStore.backgroundTasks 驱动
 * （AgentLivePanel + useMessages 的 task_* 事件 handler）。
 * 如后续需要独立任务面板，可基于此 store 接入；否则可安全移除。
 */

import { create } from 'zustand';
import type { Task, TaskStatus } from '@/types';

interface TaskState {
  // 任务列表
  tasks: Map<string, Task>;
  
  // 当前活跃任务
  activeTaskId: string | null;
  
  // Actions
  addTask: (task: Task) => void;
  updateTask: (id: string, updates: Partial<Task>) => void;
  removeTask: (id: string) => void;
  setActiveTask: (id: string | null) => void;
  getTask: (id: string) => Task | undefined;
  
  // 输出追加
  appendOutput: (id: string, output: string) => void;
  
  // 状态更新
  setTaskStatus: (id: string, status: TaskStatus) => void;
  completeTask: (id: string, result: Task['result']) => void;
  failTask: (id: string, error: string) => void;
}

export const useTaskStore = create<TaskState>((set, get) => ({
  // 初始状态
  tasks: new Map(),
  activeTaskId: null,
  
  // 添加任务
  addTask: (task) => {
    set((state) => {
      const newTasks = new Map(state.tasks);
      newTasks.set(task.id, task);
      return { tasks: newTasks };
    });
  },
  
  // 更新任务
  updateTask: (id, updates) => {
    set((state) => {
      const newTasks = new Map(state.tasks);
      const task = newTasks.get(id);
      if (task) {
        newTasks.set(id, { ...task, ...updates });
      }
      return { tasks: newTasks };
    });
  },
  
  // 移除任务
  removeTask: (id) => {
    set((state) => {
      const newTasks = new Map(state.tasks);
      newTasks.delete(id);
      return { tasks: newTasks };
    });
  },
  
  // 设置活跃任务
  setActiveTask: (id) => set({ activeTaskId: id }),
  
  // 获取任务
  getTask: (id) => get().tasks.get(id),
  
  // 追加输出
  appendOutput: (id, output) => {
    set((state) => {
      const newTasks = new Map(state.tasks);
      const task = newTasks.get(id);
      if (task) {
        // 限制输出缓冲区大小
        let buffer = task.outputBuffer + output;
        if (buffer.length > 5000) {
          const lines = buffer.split('\n');
          if (lines.length > 50) {
            buffer = '... (已省略) \n' + lines.slice(-50).join('\n');
          }
        }
        newTasks.set(id, { ...task, outputBuffer: buffer });
      }
      return { tasks: newTasks };
    });
  },
  
  // 设置任务状态
  setTaskStatus: (id, status) => {
    set((state) => {
      const newTasks = new Map(state.tasks);
      const task = newTasks.get(id);
      if (task) {
        newTasks.set(id, { ...task, status });
      }
      return { tasks: newTasks };
    });
  },
  
  // 完成任务
  completeTask: (id, result) => {
    set((state) => {
      const newTasks = new Map(state.tasks);
      const task = newTasks.get(id);
      if (task) {
        newTasks.set(id, {
          ...task,
          status: 'completed',
          completedAt: Date.now(),
          duration: Date.now() - task.startedAt,
          result,
        });
      }
      return { tasks: newTasks };
    });
    
    // 如果是活跃任务，清除
    if (get().activeTaskId === id) {
      set({ activeTaskId: null });
    }
  },
  
  // 任务失败
  failTask: (id, error) => {
    set((state) => {
      const newTasks = new Map(state.tasks);
      const task = newTasks.get(id);
      if (task) {
        newTasks.set(id, {
          ...task,
          status: 'failed',
          completedAt: Date.now(),
          result: {
            success: false,
            error,
          },
        });
      }
      return { tasks: newTasks };
    });
    
    // 如果是活跃任务，清除
    if (get().activeTaskId === id) {
      set({ activeTaskId: null });
    }
  },
}));